import React from 'react';

import { useQueryStates } from 'nuqs';

import {
  type EmployeeDataInfoFragment,
  type EmployeeSearchCompanyFilterInput,
  type EmployeeSearchMatchInfoFragment,
  useGetEmployeeDataOnEmployeeSearchForAdminLazyQuery,
  useGetEmployeeDataOnEmployeeSearchForEmployerScopeLazyQuery,
  useSearchEmployeesForAdminQuery,
  useSearchEmployeesForEmployerScopeQuery,
} from 'src/api';
import type { EmployeeSearchCriteria } from 'src/types/employeeSearch';
import { criteriaToInput } from 'src/utils/employeeSearch';
import {
  criteriaToUrlState,
  employeeSearchUrlKeys,
  employeeSearchUrlParsers,
  isDirectEmployeeCriteria,
  isSameEmployeeSearchUrlState,
  urlStateToCriteria,
} from 'src/utils/employeeSearchUrl';

// ponytail: fixed page. The schema exposes `employmentRecordsCursor`; add a
// "load more" only if someone turns up with more employers than this.
const EMPLOYMENT_RECORDS_LIMIT = 50;

type EmployeeDataSearchScope = 'admin' | 'employerScope';

type UseEmployeeDataSearchParams = {
  scope: EmployeeDataSearchScope;
  /** Admin detail pages scope the search to a single company. */
  companyFilter?: EmployeeSearchCompanyFilterInput;
  /** Extra gate, e.g. a route param id that hasn't resolved yet. */
  skip?: boolean;
};

export type UseEmployeeDataSearchResult = {
  criteria: EmployeeSearchCriteria | null;
  onRunSearch: (criteria: EmployeeSearchCriteria) => void;
  /** The URL addresses one employee by id; searching is off in this mode. */
  directMode: boolean;
  /** Drops the search from the URL, which also leaves direct mode. */
  onClearSearch: () => void;
  searchLoading: boolean;
  matches?: EmployeeSearchMatchInfoFragment[];
  selectedSsnEncrypted?: string;
  onSelectMatch: (ssnEncrypted: string) => void;
  employeeData?: EmployeeDataInfoFragment;
  employeeDataLoading: boolean;
  errorMessage?: string;
  /** Re-fetches the selected employee's data, e.g. after sending an invite. */
  refetchEmployeeData: () => void;
};

/**
 * Owns the employee search → match selection → employment-data fetch flow that
 * every employees page shares. Runs the search query for the given `scope`,
 * then fetches the full employment data for the selected match (defaulting to
 * the first). In-flight data fetches are aborted when the selection changes, so
 * rapidly clicking through matches doesn't race.
 *
 * The last submitted search lives in the URL, so a reload or a shared link
 * reproduces it — except for the full SSN, which is never persisted (see
 * `utils/employeeSearchUrl`).
 */
export const useEmployeeDataSearch = ({
  scope,
  companyFilter,
  skip = false,
}: UseEmployeeDataSearchParams): UseEmployeeDataSearchResult => {
  const isAdmin = scope === 'admin';

  const [urlState, setUrlState] = useQueryStates(employeeSearchUrlParsers, {
    urlKeys: employeeSearchUrlKeys,
    // Searches are refinements of each other, not steps to walk back through.
    history: 'replace',
  });

  // The criteria this tab actually submitted, at full fidelity. The URL holds
  // everything but the SSN, so it can't be the only source of truth.
  const [sessionCriteria, setSessionCriteria] = React.useState<EmployeeSearchCriteria | null>(null);

  // Trust the session copy only while the URL still describes that same search.
  // Anything that moves the URL out from under it — reload, back/forward, or
  // clicking "Employees" in the sidebar while already here — falls back to the
  // URL, so the results always match the address bar.
  const sessionUrlState = sessionCriteria && criteriaToUrlState(sessionCriteria);
  const criteria =
    sessionCriteria && sessionUrlState && isSameEmployeeSearchUrlState(sessionUrlState, urlState)
      ? sessionCriteria
      : urlStateToCriteria(urlState);

  const onRunSearch = React.useCallback(
    (next: EmployeeSearchCriteria) => {
      setSessionCriteria(next);
      void setUrlState(criteriaToUrlState(next));
    },
    [setUrlState],
  );

  // Clearing every param is how the screen gets back from direct mode to a
  // search — on whichever page it's mounted, without knowing the route.
  const onClearSearch = React.useCallback(() => {
    setSessionCriteria(null);
    void setUrlState(null);
  }, [setUrlState]);

  // The user's explicit pick; resolved against the current matches below.
  const [pickedSsnEncrypted, setPickedSsnEncrypted] = React.useState<string | null>(null);

  // The pages rebuild the filter object every render; the data fetch below uses it as an effect
  // dependency, so pin it to its two values.
  const companyId = companyFilter?.companyId;
  const companyType = companyFilter?.companyType;
  const scopedCompanyFilter = React.useMemo(
    () =>
      companyId !== undefined && companyType !== undefined ? { companyId, companyType } : undefined,
    [companyId, companyType],
  );

  const variables = criteria
    ? { input: criteriaToInput(criteria), companyFilter: scopedCompanyFilter }
    : undefined;

  const adminSearch = useSearchEmployeesForAdminQuery({
    variables,
    skip: !isAdmin || !criteria || skip,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const employerSearch = useSearchEmployeesForEmployerScopeQuery({
    variables,
    skip: isAdmin || !criteria || skip,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const search = isAdmin ? adminSearch : employerSearch;
  const matches = isAdmin
    ? adminSearch.data?.searchEmployeesForAdmin
    : employerSearch.data?.searchEmployeesForEmployerScope;

  // Resolve the pick against the current results: keep it while it's still in
  // the list, otherwise fall back to the first match (covers the first render
  // and every new search). Deriving this instead of resetting on completion
  // keeps the selection self-correcting and free of a stale-data flash.
  const selectedSsnEncrypted =
    pickedSsnEncrypted && matches?.some(match => match.ssnEncrypted === pickedSsnEncrypted)
      ? pickedSsnEncrypted
      : matches?.[0]?.ssnEncrypted;

  // Lazy queries don't fire until executed, so declaring both scopes is free.
  const [fetchAdminData, adminData] = useGetEmployeeDataOnEmployeeSearchForAdminLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [fetchEmployerData, employerData] =
    useGetEmployeeDataOnEmployeeSearchForEmployerScopeLazyQuery({ fetchPolicy: 'no-cache' });

  // The data fetch carries the same company filter as the search: on a company detail page only that
  // company's employment records belong on screen, not every employer the person has worked for.
  const fetchEmployeeData = React.useCallback(
    (employeeSsnEncrypted: string, signal?: AbortSignal) => {
      const options = {
        variables: {
          employeeSsnEncrypted,
          limit: EMPLOYMENT_RECORDS_LIMIT,
          companyFilter: scopedCompanyFilter,
        },
        context: signal ? { fetchOptions: { signal } } : undefined,
      };
      return isAdmin ? fetchAdminData(options) : fetchEmployerData(options);
    },
    [isAdmin, fetchAdminData, fetchEmployerData, scopedCompanyFilter],
  );

  const dataResult = isAdmin ? adminData : employerData;
  const rawEmployeeData = isAdmin
    ? adminData.data?.getEmployeeDataOnEmployeeSearchForAdmin.result
    : employerData.data?.getEmployeeDataOnEmployeeSearchForEmployerScope.result;

  // `no-cache` only bypasses Apollo's normalized store; the hook still holds its
  // last result in `.data` (hook-local state) until the next fetch completes. So
  // it must be tied to the current selection — otherwise a new search that
  // resolves to a different match, or to no matches at all (the fetch effect
  // never fires), leaks the prior search's data through as a stale result.
  const dataMatchesSelection =
    !!selectedSsnEncrypted && dataResult.variables?.employeeSsnEncrypted === selectedSsnEncrypted;
  const employeeData = dataMatchesSelection ? rawEmployeeData : undefined;

  // The data fetch fires from the effect below, one render after the search
  // resolves. Treat "a match is selected but its data isn't loaded yet" as
  // loading so the results panel never flashes its empty state in that gap (and
  // doesn't show stale data when switching between matches).
  const employeeDataLoading =
    !!selectedSsnEncrypted && (dataResult.loading || !dataMatchesSelection);

  React.useEffect(() => {
    if (!selectedSsnEncrypted) return;

    const controller = new AbortController();
    fetchEmployeeData(selectedSsnEncrypted, controller.signal).catch(() => {
      // The previous request rejects with an AbortError when the selection
      // changes mid-flight; swallow it so it isn't an unhandled rejection.
    });

    return () => controller.abort();
  }, [selectedSsnEncrypted, fetchEmployeeData]);

  // The employee data query is `no-cache`, so mutations (e.g. sending an invite)
  // can't update it through the store — re-run the fetch explicitly.
  const refetchEmployeeData = React.useCallback(() => {
    if (!selectedSsnEncrypted) return;
    void fetchEmployeeData(selectedSsnEncrypted).catch(() => {});
  }, [selectedSsnEncrypted, fetchEmployeeData]);

  return {
    criteria,
    onRunSearch,
    directMode: isDirectEmployeeCriteria(criteria),
    onClearSearch,
    searchLoading: search.loading,
    matches,
    selectedSsnEncrypted,
    onSelectMatch: setPickedSsnEncrypted,
    employeeData,
    employeeDataLoading,
    errorMessage: search.error?.message,
    refetchEmployeeData,
  };
};
